import PropTypes from 'prop-types'
import { useRef, useState, useEffect, useCallback } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { Icon } from '@iconify/react'
import bellFill from '@iconify/icons-eva/bell-fill'
import clockFill from '@iconify/icons-eva/clock-fill'
import doneAllFill from '@iconify/icons-eva/done-all-fill'
import { getSocketConnection } from 'src/utils/socket'
// material
import { alpha } from '@material-ui/core/styles'
import {
  Box,
  List,
  Badge,
  Avatar,
  Tooltip,
  Divider,
  ListItem,
  Typography,
  ListItemText,
  ListItemAvatar,
  Button,
  Stack,
} from '@material-ui/core'
// components
import Scrollbar from '../../components/Scrollbar'
import MenuPopover from '../../components/MenuPopover'
import { MIconButton } from '../../components/@material-extend'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { REST_API } from '../../constants/DefaultValues'
import { PATH_DASHBOARD } from 'src/routes/paths'
import useAuth from 'src/hooks/useAuth'   // ✅ useAuth for currentUser
import { useSnackbar } from 'notistack'

// ----------------------------------------------------------------------

function renderContent(notification) {
  return {
    avatar: (
      <img
        alt={notification.title}
        src="/static/icons/ic_notification_package.svg"
      />
    ),
    title: <Typography variant="subtitle2">{notification.title}</Typography>,
    description: (
      <Typography
        component="span"
        variant="body2"
        sx={{ color: 'text.secondary' }}
      >
        {notification.message}
      </Typography>
    ),
  }
}

NotificationItem.propTypes = {
  notification: PropTypes.object.isRequired,
  onAccept: PropTypes.func,
  onReject: PropTypes.func,
}

function NotificationItem({ notification, onAccept, onReject }) {
  const { avatar, title, description } = renderContent(notification)

  return (
    <ListItem
      disableGutters
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Box sx={{ display: 'flex', width: '100%' }}>
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: 'background.neutral' }}>{avatar}</Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={title}
          secondary={
            <>
              {description}
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  color: 'text.disabled',
                }}
              >
                <Box
                  component={Icon}
                  icon={clockFill}
                  sx={{ mr: 0.5, width: 16, height: 16 }}
                />
                {formatDistanceToNow(new Date(notification.created_on * 1000))}
              </Typography>
            </>
          }
        />
      </Box>

      {/* Action Buttons — hide after cancel/timeout (expired booking or empty actions) */}
      {Number(notification.booking_expired) !== 1 &&
        notification.actions &&
        notification.actions.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, ml: 7 }}>
          {notification.actions.includes('accept') && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => onAccept && onAccept(notification)}
            >
              Accept
            </Button>
          )}
          {notification.actions.includes('reject') && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => onReject && onReject(notification)}
            >
              Reject
            </Button>
          )}
        </Stack>
      )}
      {Number(notification.booking_expired) === 1 && (
        <Typography
          variant="caption"
          sx={{ mt: 1, ml: 7, color: 'error.main', fontWeight: 600 }}
        >
          Trip timed out — Accept is no longer available
        </Typography>
      )}
    </ListItem>
  )
}

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const navigate = useNavigate()
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [totalUnRead, setTotalUnRead] = useState(0)
  const { user: currentUser } = useAuth()   // ✅ get user from auth
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  // Load only unread notifications for dropdown
  const loadNotifications = useCallback(async () => {
    const user = currentUser
    if (!user?.id) return

    try {
      const res = await axios.post(
        `${REST_API}notifications/get-unread-notifications`,
        {
          user_id: user.id,
          user_type: user.user_type,
        },
      )

      if (res.data.status === 1) {
        setNotifications(res.data.data)
      }

      const countRes = await axios.post(
        `${REST_API}notifications/get-notification-count`,
        {
          user_id: user.id,
          user_type: user.user_type,
        },
      )

      if (countRes.data.status === 1) {
        setTotalUnRead(countRes.data.data.count)
      }
    } catch (err) {
      console.error('❌ Error fetching notifications:', err)
    }
  }, [currentUser?.id, currentUser?.user_type])

  // Initial load + Socket.IO realtime updates (no 7s polling)
  useEffect(() => {
    if (!currentUser?.id) return undefined

    loadNotifications()

    const { socket } = getSocketConnection()

    socket.on('connect', () => {
      // Server auto-joins user_${id}; emit kept for compatibility (validated server-side)
      socket.emit('join_user_room', currentUser.id)
    })

    const refresh = () => loadNotifications()
    socket.on('new_notification', refresh)
    socket.on('notification_updated', refresh)
    socket.on('notification_removed', refresh)

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      socket.off('new_notification', refresh)
      socket.off('notification_updated', refresh)
      socket.off('notification_removed', refresh)
      socket.disconnect()
    }
  }, [currentUser?.id, loadNotifications])

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const handleMarkAllAsRead = async () => {
    if (!currentUser) return

    try {
      await axios.post(`${REST_API}notifications/mark-all-as-read`, {
        user_id: currentUser.id,
        user_type: currentUser.user_type,
      })

      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })))
      setTotalUnRead(0)
    } catch (err) {
      console.error('❌ Error marking all as read:', err)
    }
  }

  const handleAcceptTrip = async (notification) => {
    if (!currentUser) return
    setOpen(false) // Close popover

    enqueueSnackbar('Are you sure you want to accept this booking?', {
      variant: 'warning',
      persist: true,
      action: (key) => (
        <div style={{ pointerEvents: 'all', display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            color="inherit"
            style={{ pointerEvents: 'all' }}
            onClick={() => closeSnackbar(key)}
          >
            Cancel
          </Button>
          <Button
            size="small"
            color="primary"
            style={{ pointerEvents: 'all' }}
            onClick={async () => {
              closeSnackbar(key)
              try {
                const res = await axios.post(`${REST_API}trips/accept-trip`, {
                  booking_id: notification.reference_id,
                  user_id: currentUser.id,
                })

                if (res.data.status === 1) {
                  enqueueSnackbar('Trip accepted successfully!', { variant: 'success' })
                  // Mark this notification as read or remove it
                  await axios.post(`${REST_API}notifications/mark-as-read`, {
                    notification_id: notification.id,
                    user_id: currentUser.id,
                  })
                  loadNotifications()
                  navigate(PATH_DASHBOARD.general.bookings)
                } else {
                  enqueueSnackbar(res.data.message || 'Failed to accept trip', {
                    variant: 'error',
                  })
                }
              } catch (err) {
                console.error('❌ Error accepting trip:', err)
                enqueueSnackbar('Error accepting trip', { variant: 'error' })
              }
            }}
          >
            OK
          </Button>
        </div>
      ),
    })
  }

  const handleRejectNotification = (notification) => {
    if (!currentUser) return;
    setOpen(false); // Close the popover to remove its click-blocking backdrop

    enqueueSnackbar('Are you sure you want to reject this booking?', {
      variant: 'warning',
      persist: true,
      action: (key) => (
        <div style={{ pointerEvents: 'all', display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            color="inherit"
            style={{ pointerEvents: 'all' }}
            onClick={() => closeSnackbar(key)}
          >
            Cancel
          </Button>
          <Button
            size="small"
            color="primary"
            style={{ pointerEvents: 'all' }}
            onClick={async () => {
              closeSnackbar(key);
              try {
                await axios.post(`${REST_API}notifications/mark-as-read`, {
                  notification_id: notification.id,
                  user_id: currentUser.id,
                });
                
                await axios.post(`${REST_API}notifications/delete-notification`, {
                  notification_id: [notification.id],
                  user_id: currentUser.id,
                  user_type: currentUser.user_type,
                });
                
                enqueueSnackbar('Rejected: Trip has been removed from your list.', { variant: 'info' });
                loadNotifications();
              } catch (err) {
                console.error('❌ Error rejecting notification:', err);
              }
            }}
          >
            OK
          </Button>
        </div>
      ),
    });
  }

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={handleOpen}
        color={open ? 'primary' : 'default'}
        sx={{
          ...(open && {
            bgcolor: (theme) =>
              alpha(
                theme.palette.primary.main,
                theme.palette.action.focusOpacity,
              ),
          }),
        }}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Icon icon={bellFill} width={20} height={20} />
        </Badge>
      </MIconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 360 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notifications</Typography>
          </Box>

          {totalUnRead > 0 && (
            <Tooltip title="Mark all as read">
              <MIconButton color="primary" onClick={handleMarkAllAsRead}>
                <Icon icon={doneAllFill} width={20} height={20} />
              </MIconButton>
            </Tooltip>
          )}
        </Box>

        <Divider />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
          <List disablePadding>
            {notifications.slice(0, 3).map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onAccept={handleAcceptTrip}
                onReject={handleRejectNotification}
              />
            ))}
            {notifications.length > 0 && <Divider />}
            <Box sx={{ pl: 1, pr: 1, width: '100%' }}>
              <Button
                component={RouterLink}
                fullWidth
                variant="outlined"
                to="/dashboard/notifications"
                onClick={handleClose}
                sx={{ textAlign: 'center', my: 1 }}
              >
                View All
                {totalUnRead > 3 && ` (${totalUnRead - 3} more)`}
              </Button>
            </Box>
          </List>
        </Scrollbar>

        <Divider />
      </MenuPopover>
    </>
  )
}
